import { useCallback, useMemo, useState } from "react";

import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useMount } from "@/Common/CustomHooks/useMount";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { ILoginModel } from "@/Common/Models/UI/Login/ILoginModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { AuthenticatorCodeValidator } from "@/Common/Validators/AuthenticatorCodeValidator";
import { Button, CheckboxLabel, LinkButton } from "@/DesignSystem";
import { Label } from "@/DesignSystem/Molecules/ToolBox/Label";
import { LoginClient } from "../Api/LoginClient";

import styles from "./LoginCredentialsView.module.scss";

interface ILoginAuthenticatorViewProps {
    loginPageModel: ILoginModel;
    stringProvider: ResourceProvider;
    errorMessageJsx?: JSX.Element;
    loginOperation: LocalOperations.Login;
}

export function LoginAuthenticatorView({
    loginPageModel,
    stringProvider,
    errorMessageJsx,
    loginOperation
}: Readonly<ILoginAuthenticatorViewProps>): JSX.Element {
    const [sendByEmailLinkDisabled, setSendByEmailLinkDisabled] = useState(false);
    const { getText } = useTranslation(stringProvider);
    const authenticatorCodeValidator = useMemo(() => new AuthenticatorCodeValidator(), []);

    const isValidatingCredentials = loginPageModel.get("ValidatingCredentials");
    const isLoggedIn = loginPageModel.get("LoggedIn");
    const authenticatorCode = loginPageModel.get("AuthenticatorCode")?.trim() || "";
    const skipAuthenticationChecked = !!loginPageModel.get("AuthenticatorCodeSkipChecked");
    const codeLength = authenticatorCode.length;
    const isAuthenticatorCodeValid = authenticatorCodeValidator.validate(authenticatorCode);
    const errorMessage = loginPageModel.get("ErrorMessage").Message;
    const isInvalid = errorMessage || codeLength !== AuthenticatorCodeValidator.codeLength;
    const inputFormCssClass = `form-control ${isInvalid ? "invalid" : ""} ${codeLength !== 0 ? styles.letterSpace : ""}`;

    const showCodeInput = !isValidatingCredentials && !isLoggedIn;
    const showSendCodeByEmailLink = showCodeInput && !cacheStore.ClinicSettings.EnableAppOnlyMultifactorAuthentication;

    const showMultifactorAuthenticationSkip = showCodeInput && cacheStore.ClinicSettings.EnableMultifactorAuthenticationSkip;

    let skipAuthenticationLabel = "";
    if (showMultifactorAuthenticationSkip) {
        skipAuthenticationLabel = cacheStore.ClinicSettings.MultifactorAuthenticationSkipDurationDays === 1
            ? getText("MultifactorAuthentication_SkipForOneDayLabel")
            : getText("MultifactorAuthentication_SkipForXDaysLabel").replaceAll("{{0}}", `${cacheStore.ClinicSettings.MultifactorAuthenticationSkipDurationDays}`);
    }
    const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
        if (!isValidatingCredentials && authenticatorCode && event.key === "Enter") {
            sessionStore.dispatch(loginOperation);
        }
    }, [isValidatingCredentials, authenticatorCode, loginOperation]);

    const handleAuthenticatorCodeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/\D/g, "").slice(0, AuthenticatorCodeValidator.codeLength);
        sessionStore.dispatch(LocalOperations.Login.AuthenticatorCode_TextChange, { Value: value });
    }, []);

    const handleSkipAuthenticationCheckedChange = useCallback((value: boolean) => {
        sessionStore.dispatch(LocalOperations.Login.AuthenticatorCode_SkipCheckedChange, { Value: value });
    }, []);

    const renderVisualCode = (): string => {
        if (!authenticatorCode) {
            return null;
        }

        const visualArray = Array.from({ length: AuthenticatorCodeValidator.codeLength }, (_, i) => authenticatorCode[i] || "_");
        return visualArray.join("");
    };

    const sendCodeByEmail = useCallback(async (): Promise<void> => {
        try {
            const response = await LoginClient.sendAuthenticationCodeByEmail();
            if (!response) {
                displayToast(MessageToast.Error, getText("InternalServerError"));
                return;
            }

            displayToast(MessageToast.Success, getText("CodeSentLabel"), {
                hideProgressBar: false,
                pauseOnFocusLoss: false,
                autoClose: 3000,
                pauseOnHover: false,
                closeOnClick: false
            });
        }
        catch {
            displayToast(MessageToast.Error, getText("InternalServerError"));
        }
    }, [getText]);

    const handleSendCodeByEmail = useCallback(async (): Promise<void> => {
        if (sendByEmailLinkDisabled)
            return;

        setSendByEmailLinkDisabled(true);
        await sendCodeByEmail();
        setTimeout(() => setSendByEmailLinkDisabled(false), 3000);
    }, [sendByEmailLinkDisabled, sendCodeByEmail]);

    useMount(() => {
        if (
            cacheStore.ClinicSettings?.EnableMultifactorAuthenticationByEmail &&
            sessionStore.clientLoginModel.get("SelectedAuthenticatorCodeDeliveryMethod") === AuthenticatorCodeDeliveryMethod.Email
        ) {
            sendCodeByEmail();
        }
    });

    return (
        <>
            <div className={styles.loginRow}>
                <Label
                    htmlFor="user_name"
                    label={getText("login_AuthenticatorDigitCodelabel")}
                />
                {showCodeInput && (
                    <div className={styles.authenticatorCodeContainer}>
                        <div className={`${styles.visualCode} ${inputFormCssClass}`}>
                            {renderVisualCode()}
                        </div>
                        <input
                            type="text"
                            id="AuthenticatorCode"
                            data-cy="login-authenticator-code-text-input"
                            onKeyUp={handleKeyPress}
                            className={`${styles.authenticatorCode} ${inputFormCssClass}`}
                            maxLength={AuthenticatorCodeValidator.codeLength}
                            placeholder={getText("AuthenticatorCodePlaceholder")}
                            onChange={handleAuthenticatorCodeChange}
                            value={authenticatorCode}
                            autoComplete="off"
                            autoFocus
                        />
                    </div>
                )}
            </div>

            {showMultifactorAuthenticationSkip && (
                <div className={styles.loginRow}>
                    <div className={styles.authenticatorCodeContainer}>
                        <CheckboxLabel
                            checked={skipAuthenticationChecked}
                            label={skipAuthenticationLabel}
                            onChange={checked => handleSkipAuthenticationCheckedChange(checked)}
                            dataCy="login-skip-authentication-checkbox"
                        />
                    </div>
                </div>
            )}
            {errorMessageJsx}

            <div className={styles.loginButtonContainer}>
                <Button
                    variant="brand"
                    disabled={!isAuthenticatorCodeValid || isValidatingCredentials || isLoggedIn}
                    id="LoginVerifyButton"
                    dataCy="login-authenticator-verify-button"
                    onClick={() => sessionStore.dispatch(loginOperation)}
                    label={getText("Login_VerifyCodeLabel")}
                    size="lg"
                />
                {showSendCodeByEmailLink && (
                    <div className={styles.sendByEmailLink}>
                        <LinkButton
                            label={getText("Login_SendByEmailLabel")}
                            onClick={handleSendCodeByEmail}
                            disabled={sendByEmailLinkDisabled}
                            dataCy="login-authenticator-send-by-email-link"
                        />
                    </div>
                )}
            </div>
        </>
    );
}
